import React, { useState } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Text,
    Card,
    Field,
    Checkbox,
    RadioGroup,
    Radio,
    Badge,
    ProgressBar,
    Divider,
} from '@fluentui/react-components';
import {
    VideoClip24Regular,
    Mic24Regular,
    Globe24Regular,
    Shield24Regular,
    Document24Regular,
    Call24Regular,
    Dismiss24Regular,
    Info24Regular,
} from '@fluentui/react-icons';

interface ConsentOptions {
    audioRecording: boolean;
    videoRecording: boolean;
    transcription: boolean;
    translation: boolean;
    aiAnalysis: boolean;
    dataSharing: 'none' | 'internal' | 'external' | 'research';
    storageRetention: '30days' | '90days' | '1year' | '7years';
}

interface ConsentCaptureProps {
    isOpen: boolean;
    patientName: string;
    consultationType: string;
    language: string;
    interpreterRequired: boolean;
    onConsent: (consentData: ConsentOptions) => void;
    onCancel: () => void;
    loading?: boolean;
}

export const ConsentCapture: React.FC<ConsentCaptureProps> = ({
    isOpen,
    patientName,
    consultationType,
    language,
    interpreterRequired,
    onConsent,
    onCancel,
    loading = false,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [consentOptions, setConsentOptions] = useState<ConsentOptions>({
        audioRecording: false,
        videoRecording: false,
        transcription: false,
        translation: false,
        aiAnalysis: false,
        dataSharing: 'internal',
        storageRetention: '1year',
    });

    const [acknowledgments, setAcknowledgments] = useState({
        understandPurpose: false,
        reviewedRights: false,
        voluntaryParticipation: false,
        dataHandling: false,
    });

    const totalSteps = 3;
    const progress = (currentStep / totalSteps) * 100;

    const handleConsentChange = (key: keyof ConsentOptions, value: any) => {
        setConsentOptions((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleAcknowledgmentChange = (key: keyof typeof acknowledgments, value: boolean) => {
        setAcknowledgments((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const canProceedToNext = () => {
        if (currentStep === 1) return true;
        if (currentStep === 2) return true;
        if (currentStep === 3) {
            return Object.values(acknowledgments).every((ack) => ack === true);
        }
        return false;
    };

    const canComplete = () => {
        return canProceedToNext() && currentStep === totalSteps;
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () => {
        if (canComplete()) {
            onConsent(consentOptions);
        }
    };

    const renderStep1 = () => (
        <div>
            <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                Recording and Documentation Consent
            </Text>
            <Text size={400} style={{ marginBottom: '20px', color: '#605e5c' }}>
                Please select which recording and analysis features you consent to for this
                consultation:
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Card style={{ padding: '16px' }}>
                    <Field>
                        <Checkbox
                            checked={consentOptions.audioRecording}
                            onChange={(_, data) =>
                                handleConsentChange('audioRecording', data.checked === true)
                            }
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Mic24Regular style={{ color: '#0078d4' }} />
                                    <span>Audio Recording</span>
                                    <Badge size="small" appearance="tint" color="brand">
                                        Recommended
                                    </Badge>
                                </div>
                            }
                        />
                    </Field>
                    <Text size={300} style={{ marginLeft: '32px', color: '#605e5c' }}>
                        Record audio for clinical documentation and quality assurance
                    </Text>
                </Card>

                <Card style={{ padding: '16px' }}>
                    <Field>
                        <Checkbox
                            checked={consentOptions.videoRecording}
                            onChange={(_, data) =>
                                handleConsentChange('videoRecording', data.checked === true)
                            }
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <VideoClip24Regular style={{ color: '#0078d4' }} />
                                    <span>Video Recording</span>
                                </div>
                            }
                        />
                    </Field>
                    <Text size={300} style={{ marginLeft: '32px', color: '#605e5c' }}>
                        Record video for comprehensive clinical assessment (optional)
                    </Text>
                </Card>

                <Card style={{ padding: '16px' }}>
                    <Field>
                        <Checkbox
                            checked={consentOptions.transcription}
                            onChange={(_, data) =>
                                handleConsentChange('transcription', data.checked === true)
                            }
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Document24Regular style={{ color: '#0078d4' }} />
                                    <span>Real-time Transcription</span>
                                    <Badge size="small" appearance="tint" color="brand">
                                        Recommended
                                    </Badge>
                                </div>
                            }
                        />
                    </Field>
                    <Text size={300} style={{ marginLeft: '32px', color: '#605e5c' }}>
                        Convert speech to text for accurate clinical notes
                    </Text>
                </Card>

                {interpreterRequired && (
                    <Card style={{ padding: '16px', backgroundColor: '#fff4e6' }}>
                        <Field>
                            <Checkbox
                                checked={consentOptions.translation}
                                onChange={(_, data) =>
                                    handleConsentChange('translation', data.checked === true)
                                }
                                label={
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <Globe24Regular style={{ color: '#ffa500' }} />
                                        <span>Language Translation ({language})</span>
                                        <Badge size="small" appearance="filled" color="warning">
                                            Required
                                        </Badge>
                                    </div>
                                }
                            />
                        </Field>
                        <Text size={300} style={{ marginLeft: '32px', color: '#605e5c' }}>
                            Real-time translation between English and {language}
                        </Text>
                    </Card>
                )}

                <Card style={{ padding: '16px' }}>
                    <Field>
                        <Checkbox
                            checked={consentOptions.aiAnalysis}
                            onChange={(_, data) =>
                                handleConsentChange('aiAnalysis', data.checked === true)
                            }
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Shield24Regular style={{ color: '#107c10' }} />
                                    <span>AI Clinical Analysis</span>
                                </div>
                            }
                        />
                    </Field>
                    <Text size={300} style={{ marginLeft: '32px', color: '#605e5c' }}>
                        Sentiment analysis, mood detection, and clinical insights (optional)
                    </Text>
                </Card>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div>
            <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                Data Handling and Storage
            </Text>
            <Text size={400} style={{ marginBottom: '20px', color: '#605e5c' }}>
                Please specify your preferences for data sharing and retention:
            </Text>

            <Card style={{ padding: '20px', marginBottom: '20px' }}>
                <Text size={400} weight="medium" style={{ marginBottom: '12px' }}>
                    Data Sharing Permissions
                </Text>
                <RadioGroup
                    value={consentOptions.dataSharing}
                    onChange={(_, data) => handleConsentChange('dataSharing', data.value)}
                >
                    <Radio value="internal" label="Internal medical team only" />
                    <Radio value="external" label="External specialists as needed" />
                    <Radio value="research" label="De-identified research purposes" />
                    <Radio value="none" label="No data sharing" />
                </RadioGroup>
            </Card>

            <Card style={{ padding: '20px' }}>
                <Text size={400} weight="medium" style={{ marginBottom: '12px' }}>
                    Data Retention Period
                </Text>
                <RadioGroup
                    value={consentOptions.storageRetention}
                    onChange={(_, data) => handleConsentChange('storageRetention', data.value)}
                >
                    <Radio value="30days" label="30 days (minimum for clinical care)" />
                    <Radio value="90days" label="90 days (standard)" />
                    <Radio value="1year" label="1 year (recommended)" />
                    <Radio value="7years" label="7 years (maximum legal retention)" />
                </RadioGroup>
            </Card>

            <Card style={{ padding: '16px', backgroundColor: '#f0f8ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Info24Regular style={{ color: '#0078d4' }} />
                    <Text size={400} weight="medium">
                        Important Information
                    </Text>
                </div>
                <Text size={300} style={{ marginTop: '8px', color: '#605e5c' }}>
                    All data is encrypted and stored securely in compliance with HIPAA regulations.
                    You can revoke consent at any time during the consultation.
                </Text>
            </Card>
        </div>
    );

    const renderStep3 = () => (
        <div>
            <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                Final Acknowledgments
            </Text>
            <Text size={400} style={{ marginBottom: '20px', color: '#605e5c' }}>
                Please review and acknowledge the following statements:
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Field>
                    <Checkbox
                        checked={acknowledgments.understandPurpose}
                        onChange={(_, data) =>
                            handleAcknowledgmentChange('understandPurpose', data.checked === true)
                        }
                        label="I understand the purpose of this teleconsultation and the technology being used"
                    />
                </Field>

                <Field>
                    <Checkbox
                        checked={acknowledgments.reviewedRights}
                        onChange={(_, data) =>
                            handleAcknowledgmentChange('reviewedRights', data.checked === true)
                        }
                        label="I have reviewed my rights regarding the recording and use of my medical information"
                    />
                </Field>

                <Field>
                    <Checkbox
                        checked={acknowledgments.voluntaryParticipation}
                        onChange={(_, data) =>
                            handleAcknowledgmentChange(
                                'voluntaryParticipation',
                                data.checked === true
                            )
                        }
                        label="I understand that my participation in recording and AI analysis is voluntary"
                    />
                </Field>

                <Field>
                    <Checkbox
                        checked={acknowledgments.dataHandling}
                        onChange={(_, data) =>
                            handleAcknowledgmentChange('dataHandling', data.checked === true)
                        }
                        label="I consent to the data handling and storage preferences I have specified"
                    />
                </Field>
            </div>

            <Divider style={{ margin: '20px 0' }} />

            <Card style={{ padding: '16px', backgroundColor: '#f8f9fa' }}>
                <Text size={400} weight="medium" style={{ marginBottom: '8px' }}>
                    Consent Summary for {patientName}
                </Text>
                <Text size={300} style={{ color: '#605e5c' }}>
                    Consultation: {consultationType} | Language: {language}
                    {interpreterRequired && ' (Interpreter Required)'}
                </Text>
                <div style={{ marginTop: '12px' }}>
                    <Text size={300}>
                        <strong>Recording:</strong>{' '}
                        {consentOptions.audioRecording ? 'Audio ✓' : 'Audio ✗'}{' '}
                        {consentOptions.videoRecording ? 'Video ✓' : 'Video ✗'}
                    </Text>
                    <Text size={300} style={{ display: 'block' }}>
                        <strong>Features:</strong>{' '}
                        {consentOptions.transcription ? 'Transcription ✓' : 'Transcription ✗'}{' '}
                        {consentOptions.translation ? 'Translation ✓' : 'Translation ✗'}{' '}
                        {consentOptions.aiAnalysis ? 'AI Analysis ✓' : 'AI Analysis ✗'}
                    </Text>
                    <Text size={300} style={{ display: 'block' }}>
                        <strong>Storage:</strong> {consentOptions.storageRetention} |{' '}
                        <strong>Sharing:</strong> {consentOptions.dataSharing}
                    </Text>
                </div>
            </Card>
        </div>
    );

    return (
        <Dialog open={isOpen}>
            <DialogSurface style={{ maxWidth: '700px', width: '90vw' }}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                                onClick={onCancel}
                            />
                        }
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Document24Regular style={{ fontSize: '24px', color: '#0078d4' }} />
                            Consultation Consent - Step {currentStep} of {totalSteps}
                        </div>
                    </DialogTitle>

                    <DialogContent>
                        <ProgressBar
                            value={progress}
                            style={{ marginBottom: '24px' }}
                            thickness="large"
                        />

                        {currentStep === 1 && renderStep1()}
                        {currentStep === 2 && renderStep2()}
                        {currentStep === 3 && renderStep3()}
                    </DialogContent>

                    <DialogActions>
                        <Button
                            appearance="secondary"
                            onClick={currentStep === 1 ? onCancel : handlePrevious}
                        >
                            {currentStep === 1 ? 'Cancel' : 'Previous'}
                        </Button>

                        {currentStep < totalSteps ? (
                            <Button
                                appearance="primary"
                                onClick={handleNext}
                                disabled={!canProceedToNext()}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                appearance="primary"
                                icon={<Call24Regular />}
                                onClick={handleComplete}
                                disabled={!canComplete() || loading}
                            >
                                {loading ? 'Starting Call...' : 'Start Consultation'}
                            </Button>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
